import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api/client';

/**
 * GET a admin endpoint and keep the result in state.
 *
 * Keeps the previous data visible while refetching, so changing a filter does
 * not blank the table and bounce the page height.
 */
export function useApi(url, params, { enabled = true, refreshMs } = {}) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(enabled);

  // Serialise params so the effect only re-runs on a real change.
  const key = JSON.stringify(params || {});
  const aborter = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    aborter.current?.abort();
    const controller = new AbortController();
    aborter.current = controller;

    setIsLoading(true);
    try {
      const res = await api.get(url, {
        params: JSON.parse(key),
        signal: controller.signal,
      });
      setData(res.data?.data ?? null);
      setMeta({ pagination: res.data?.pagination, summary: res.data?.summary });
      setError(null);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      setError(err.message || 'Could not load this data.');
    } finally {
      setIsLoading(false);
    }
  }, [url, key, enabled]);

  useEffect(() => {
    load();
    return () => aborter.current?.abort();
  }, [load]);

  useEffect(() => {
    if (!refreshMs) return undefined;
    const t = setInterval(load, refreshMs);
    return () => clearInterval(t);
  }, [refreshMs, load]);

  return { data, meta, error, isLoading, reload: load };
}

/** Debounces a value, so typing in a search box does not fire a request a key. */
export function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ===========================================================================
   Formatting
   =========================================================================== */

export const formatNumber = (n) =>
  n == null ? '—' : new Intl.NumberFormat('en-IN').format(n);

export const formatPercent = (n, digits = 1) =>
  n == null ? '—' : `${Number(n).toFixed(digits)}%`;

export function formatDate(value, { withTime = false } = {}) {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

export function timeAgo(value) {
  if (!value) return '—';
  const seconds = Math.floor((Date.now() - new Date(value)) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return formatDate(value);
}

export function formatDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

/* ===========================================================================
   Channel identity
   These hexes mirror the --ch-* custom properties in admin.css. Charts need
   real values rather than CSS variables, so both live here.
   =========================================================================== */
export const CHANNEL_COLORS = {
  direct: '#1a1d2e',
  organic_search: '#0e9384',
  paid_search: '#d92d20',
  social: '#7a5af8',
  referral: '#f79009',
  email: '#2e90fa',
  affiliate: '#dd2590',
  display: '#85888e',
  other: '#b8bcc4',
};

export const CHANNEL_LABELS = {
  direct: 'Direct',
  organic_search: 'Organic search',
  paid_search: 'Paid search',
  social: 'Social',
  referral: 'Referral',
  email: 'Email',
  affiliate: 'Affiliate',
  display: 'Display',
  other: 'Other',
};

export const channelColor = (key) => CHANNEL_COLORS[key] || CHANNEL_COLORS.other;
export const channelLabel = (key) => CHANNEL_LABELS[key] || 'Other';

/* Status → pill colour. Keeps every table consistent. */
export const STATUS_TONE = {
  new: 'blue', in_progress: 'amber', responded: 'neutral',
  converted: 'green', closed: 'neutral', spam: 'red',
  submitted: 'blue', screening: 'amber', shortlisted: 'blue',
  interview_1: 'amber', interview_2: 'amber', selected: 'green',
  offered: 'green', accepted: 'green',
  rejected: 'red', withdrawn: 'neutral',
  undecided: 'neutral', paid: 'green', unpaid: 'neutral',
  active: 'green', unsubscribed: 'neutral', bounced: 'red',
};

export const humanise = (s) =>
  String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
